import { useState, useRef, useEffect, useCallback, useMemo } from 'react';

/**
 * Manages the "Quick View" feature that shows a full-size card preview
 * when hovering over nodes that are zoomed out below a threshold.
 */
export function useQuickView({ zoom, tasks, nestedSprints, canvasRef }) {
  const [quickViewTaskId, setQuickViewTaskId] = useState(null);
  const [quickViewMousePos, setQuickViewMousePos] = useState(null);

  const QUICK_VIEW_ZOOM_THRESHOLD = 0.5;

  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;

  const quickViewTaskIdRef = useRef(quickViewTaskId);
  quickViewTaskIdRef.current = quickViewTaskId;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handlePanUpdate = (e) => {
      const { zoom: currentZoom } = e.detail;
      zoomRef.current = currentZoom;

      if (currentZoom >= QUICK_VIEW_ZOOM_THRESHOLD && quickViewTaskIdRef.current) {
        setQuickViewTaskId(null);
        setQuickViewMousePos(null);
      }
    };

    canvas.addEventListener('panupdate', handlePanUpdate);
    return () => canvas.removeEventListener('panupdate', handlePanUpdate);
  }, [canvasRef]);

  useEffect(() => {
    if (zoom >= QUICK_VIEW_ZOOM_THRESHOLD && quickViewTaskId) {
      setQuickViewTaskId(null);
      setQuickViewMousePos(null);
    }
  }, [zoom, quickViewTaskId]);

  const isQuickViewEnabled = zoomRef.current < QUICK_VIEW_ZOOM_THRESHOLD;

  const nestedSprintMap = useMemo(() => {
    const map = {};
    nestedSprints?.forEach((ns) => {
      map[ns.id] = ns;
    });
    return map;
  }, [nestedSprints]);

  const handleQuickViewMouseEnter = useCallback((taskId, e) => {
    if (zoomRef.current >= QUICK_VIEW_ZOOM_THRESHOLD) return;
    setQuickViewTaskId(taskId);
    setQuickViewMousePos({ x: e.clientX, y: e.clientY });
  }, []);

  const handleQuickViewMouseLeave = useCallback(() => {
    setQuickViewTaskId(null);
    setQuickViewMousePos(null);
  }, []);

  const handleQuickViewMouseMove = useCallback((e) => {
    if (quickViewTaskIdRef.current && zoomRef.current < QUICK_VIEW_ZOOM_THRESHOLD) {
      setQuickViewMousePos({ x: e.clientX, y: e.clientY });
    }
  }, []);

  const quickViewTask = quickViewTaskId ? tasks.find((t) => t.id === quickViewTaskId) : null;
  const quickViewNestedSprint = quickViewTask?.nestedSprintId
    ? nestedSprintMap[quickViewTask.nestedSprintId]
    : null;

  return {
    isQuickViewEnabled,
    quickViewTask,
    quickViewNestedSprint,
    quickViewMousePos,
    handleQuickViewMouseEnter,
    handleQuickViewMouseLeave,
    handleQuickViewMouseMove,
  };
}

export default useQuickView;
